// BUG-2373: where an aggregator (Swiggy/Zomato/UrbanPiper) order sits in its
// lifecycle, from the POS's point of view. Mirrors the backend's OfdStatusRank
// (cw-retail-services, aggregator/urbanpiper/OfdStatusRank.java) so the POS and
// the backend agree on what "further along" means.
//
// Unknown / missing statuses deliberately rank as -1 and are treated as "not
// decided yet" by every helper below -- a status code the POS doesn't know
// must never suppress a popup or a print for a genuinely new order.
const OFD_STATUS_RANK = {
  NEW: 10,
  PN: 20,
  CF: 30,
  AK: 40,
  IP: 50,
  FR: 60,
  PD: 70,
  AD: 80,
  PC: 90,
  DI: 100,
  OD: 110,
  DL: 130,
  CO: 130,
};

// Long-form values some rows carry instead of the code (s_order.ofd_status is
// free text; e.g. walk-in rows at GPR are "Delivered").
const OFD_STATUS_ALIASES = {
  PLACED: "NEW",
  PENDING: "PN",
  CONFIRMED: "CF",
  ACKNOWLEDGED: "AK",
  INPROGRESS: "IP",
  FOODREADY: "FR",
  DISPATCHED: "DI",
  OUTFORDELIVERY: "OD",
  DELIVERED: "DL",
  COMPLETED: "CO",
  CANCELLED: "CC",
  CANCELED: "CC",
};

export const normalizeOfdStatus = (status) => {
  if (status === null || status === undefined) return null;
  const raw = String(status).trim().toUpperCase();
  if (!raw) return null;
  const compact = raw.replace(/[\s_-]+/g, "");
  return OFD_STATUS_ALIASES[compact] || raw;
};

export const ofdStatusRank = (status) => {
  const code = normalizeOfdStatus(status);
  if (!code) return -1;
  return OFD_STATUS_RANK[code] ?? -1;
};

export const isCancelledOfdStatus = (status) => normalizeOfdStatus(status) === "CC";

/**
 * The order has moved past acknowledgement (Food Ready, Dispatched, Completed,
 * ...) or was cancelled. Its accept-time receipt/KOT went out long ago, so an
 * automatic print for it now can only be a reprint.
 * Verified on GPR live (Miyapur E1, Sep 2026): all 3,547 genuine first prints
 * happened while the order was NEW or AK, none after FR/DI/CO/CC.
 */
export const isOfdPastAcknowledged = (status) => isCancelledOfdStatus(status) || ofdStatusRank(status) > OFD_STATUS_RANK.IP;

/**
 * Whether an order recovered by the pending-order poll has already been dealt
 * with and must not get an accept popup (whose auto-accept would print it).
 * `info` is getAggregatorOrderStatus()'s result: { ofdStatus, print }, or
 * undefined when the lookup failed -- which always means "offer the popup".
 * AK on its own is deliberately NOT treated as handled: UrbanPiper moves most
 * orders to AK itself before the POS acts (2,229 of 3,560 on Miyapur E1), so an
 * AK order can still be one this till never printed. Only print="Y" proves it
 * was -- and that flag is reliable while the order is still at AK, because only
 * a later POS status change (FR/DI) overwrites it.
 */
export const isRecoveredOrderAlreadyHandled = (info) => {
  if (!info) return false;
  if (isOfdPastAcknowledged(info.ofdStatus)) return true;
  return ofdStatusRank(info.ofdStatus) >= OFD_STATUS_RANK.AK && info.print === "Y";
};
